import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { activityCategories, getActivityLabel } from "@/lib/taskActivities";

export interface ListingTask {
  id: string;
  north_id: string;
  activity_type: string;
  activity_date: string;
  title: string;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
}

interface AddTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  northId: string;
  task: ListingTask | null;
  hoverColor?: string | null;
  onSuccess: () => void;
}

export const AddTaskDialog = ({
  open,
  onOpenChange,
  northId,
  task,
  hoverColor,
  onSuccess,
}: AddTaskDialogProps) => {
  const [activityType, setActivityType] = useState<string>("professional_photography");
  const [title, setTitle] = useState("");
  const [activityDate, setActivityDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentTasks, setRecentTasks] = useState<ListingTask[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      if (task) {
        setActivityType(task.activity_type);
        setTitle(task.title);

        const utcDate = new Date(task.activity_date);
        const year = utcDate.getFullYear();
        const month = String(utcDate.getMonth() + 1).padStart(2, "0");
        const day = String(utcDate.getDate()).padStart(2, "0");
        const hours = String(utcDate.getHours()).padStart(2, "0");
        const minutes = String(utcDate.getMinutes()).padStart(2, "0");
        setActivityDate(`${year}-${month}-${day}T${hours}:${minutes}`);
        setNotes(task.notes || "");
      } else {
        setActivityType("professional_photography");
        setTitle("");
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        setActivityDate(`${year}-${month}-${day}T${hours}:${minutes}`);
        setNotes("");
      }
      fetchRecentTasks();
    }
  }, [open, task]);

  const fetchRecentTasks = async () => {
    try {
      const { data, error } = await supabase
        .from("listing_tasks" as any)
        .select("*")
        .eq("north_id", northId)
        .order("activity_date", { ascending: false })
        .limit(6);

      if (error) throw error;
      setRecentTasks((data || []) as unknown as ListingTask[]);
    } catch (error) {
      logger.error("Error fetching recent tasks:", error);
    }
  };

  // Auto-fill title from activity type when adding new (not editing)
  const handleActivityTypeChange = (value: string) => {
    setActivityType(value);
    if (!task) {
      const label = getActivityLabel(value);
      if (value !== "other") {
        setTitle(label);
      } else {
        setTitle("");
      }
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Task title is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const taskData = {
        north_id: northId,
        activity_type: activityType,
        activity_date: new Date(activityDate).toISOString(),
        title: title.trim(),
        notes: notes.trim() || null,
        updated_at: new Date().toISOString(),
      };

      if (task) {
        const { error } = await (supabase
          .from("listing_tasks" as any)
          .update(taskData)
          .eq("id", task.id) as any);

        if (error) throw error;

        toast({
          title: "Task Updated",
          description: "Task updated successfully",
        });
      } else {
        const { error } = await (supabase
          .from("listing_tasks" as any)
          .insert(taskData) as any);

        if (error) throw error;

        toast({
          title: "Task Added",
          description: "Task created successfully",
        });
      }

      onSuccess();
    } catch (error) {
      logger.error("Error saving task:", error);
      toast({
        title: "Error",
        description: "Failed to save task",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{task ? "Edit Task" : "Add Task"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Activity Type & Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select value={activityType} onValueChange={handleActivityTypeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {activityCategories.map((category) => (
                    <SelectGroup key={category.label}>
                      <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {category.label}
                      </SelectLabel>
                      {category.activities.map((activity) => (
                        <SelectItem key={activity.value} value={activity.value}>
                          {activity.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date & Time *</Label>
              <Input
                type="datetime-local"
                value={activityDate}
                onChange={(e) => setActivityDate(e.target.value)}
              />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              placeholder="e.g., Professional Photography Completed"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              placeholder="Add any details or notes about this activity..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          {/* Recent Tasks Reference */}
          {!task && recentTasks.length > 0 && (
            <div className="space-y-2 pt-4 border-t">
              <Label className="text-sm text-muted-foreground">
                Recent Tasks (for reference)
              </Label>
              <div className="space-y-2">
                {recentTasks.map((recentTask) => (
                  <div
                    key={recentTask.id}
                    className="flex items-center justify-between p-2 bg-muted/30 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {getActivityLabel(recentTask.activity_type)}
                      </Badge>
                      <span className="text-sm font-medium">{recentTask.title}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(recentTask.activity_date)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            style={{ transition: "background-color 0.2s ease-in-out" }}
            onMouseEnter={(e) => {
              if (hoverColor) {
                e.currentTarget.style.backgroundColor = hoverColor;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : task ? "Update Task" : "Add Task"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
